/* eslint-disable import/no-cycle */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Carousel } from '@mantine/carousel';
import { Grid } from '@mantine/core';
import AppStack from '../../../../common/AppStack';
import WsSectionContainer from '../WsSectionContainer';
import AppText from '../../../../common/AppText';
import { IMAGE_DISPLAY_ENUM } from './wsDesignListConfig';
import { Context as StoreUserContext } from '../../../../../providers/StoreUserContextProvider';
import WsBlock from '../../blocks/WsBlock';
import WsDesignCard from '../designGrid/WsDesignCard';
import {
  doesResourcesHaveProducts,
  getSelectedResourceBaseNavigationUrl,
  getWebstoreHeadingSize
} from '../../../../../helpers/webstoreHelper';
import WsSectionViewAllButtons from '../WsSectionViewAllButtons';

const WsDesignList = ({
  blocks,
  config,
  themeConfig,
  colorSchemes,
  mediaQueries,
  onNavigate,
  baseStoreUrl,
  ecomStoreTheme,
  resourceNavigation,
  selectedResources,
  ...rest
}) => {
  const { state } = useContext(StoreUserContext);
  const { isTabletOrSmaller } = mediaQueries;
  const colorScheme = colorSchemes[config.colorScheme - 1] ?? colorSchemes[0];
  const maxDisplayed = config.maximumProductsDisplayed * 1 ?? 20;
  const pageWidth =
    themeConfig.pageWidth && themeConfig.pageWidth * 1 >= 1000
      ? themeConfig.pageWidth * 1
      : 1000;

  const {
    ecomCategory,
    ecomSubcategory,
    ecomStoreDesign,
    ecomStoreCollection
  } = selectedResources;
  const baseResourceUrl = `${baseStoreUrl}${getSelectedResourceBaseNavigationUrl(
    resourceNavigation,
    'designs'
  )}`;

  const isBlocksEmpty = blocks.length === 0;
  const filteredBlocks = blocks.filter((b) =>
    doesResourcesHaveProducts(ecomStoreTheme, {
      pkEcomCategory: ecomCategory?.value,
      pkEcomVendorDesign: b.config?.ecomStoreDesign,
      pkEcomStoreCollection: ecomStoreCollection?.pkEcomStoreCollection,
      ecomSubcategory: ecomSubcategory?.value
    })
  );
  const filteredDesigns =
    isBlocksEmpty && !ecomStoreDesign
      ? ecomStoreTheme.ecomStore.ecomStoreDesigns
          .filter((d) =>
            doesResourcesHaveProducts(ecomStoreTheme, {
              pkEcomCategory: ecomCategory?.value,
              pkEcomVendorDesign: d?.pkEcomVendorDesign,
              pkEcomStoreCollection: ecomStoreCollection?.pkEcomStoreCollection,
              ecomSubcategory: ecomSubcategory?.value
            })
          )
          .map((d) => ({
            ...d,
            design: state.ecomStoreDesigns.value.find(
              (s) => s.pkEcomVendorDesign === d.pkEcomVendorDesign
            )
          }))
      : [];
  const designCount = isBlocksEmpty
    ? filteredDesigns.length
    : filteredBlocks.length;

  const isHidden = designCount === 0;

  return (
    !isHidden && (
      <WsSectionContainer
        colorScheme={colorScheme}
        config={{
          ...config,
          fullWidth: 'true'
        }}
        mediaQueries={mediaQueries}
        themeConfig={themeConfig}
        {...rest}
      >
        <AppStack
          style={{
            flex: 1,
            justifyContent: 'space-between',
            margin: 'auto',
            width: '100%',
            alignItems: 'center',
            gap: 16
          }}
        >
          <AppStack
            style={{
              width: '100%',
              maxWidth: config.fullWidth === 'true' ? 'unset' : pageWidth,
              gap: 5
            }}
          >
            {config.headingText && (
              <AppText
                style={{
                  fontWeight: 600,
                  fontSize: getWebstoreHeadingSize(config.headingSize)
                }}
              >
                {config.headingText}
              </AppText>
            )}
            {config.descriptionText && (
              <AppText style={{ fontSize: 16 }}>
                {config.descriptionText}
              </AppText>
            )}
          </AppStack>

          <AppStack
            style={{
              flex: 1,
              justifyContent: 'space-between',
              margin: 'auto',
              width: '100%',
              maxWidth:
                config.productsFullWidth === 'true' ? 'unset' : pageWidth,
              gap: 0
            }}
          >
            {(isTabletOrSmaller && config.mobileCarousel === 'true') ||
            (!isTabletOrSmaller && config.desktopCarousel === 'true') ? (
              <Carousel
                align={isTabletOrSmaller ? 'center' : 'start'}
                controlSize={40}
                draggable
                loop={false}
                slideGap={16}
                slideSize={`${
                  (isTabletOrSmaller ? 85 : 100) /
                  (isTabletOrSmaller
                    ? config.mobileColumnCount * 1 ?? 1
                    : config.desktopColumnCount * 1 ?? 6)
                }%`}
                slidesToScroll={
                  isTabletOrSmaller
                    ? config.mobileColumnCount * 1 ?? 1
                    : config.desktopColumnCount * 1 ?? 6
                }
                withControls={!isTabletOrSmaller}
                withIndicators={false}
              >
                {isBlocksEmpty
                  ? filteredDesigns
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .slice(0, maxDisplayed)
                      .map((d) => (
                        <Carousel.Slide key={d.pkEcomVendorDesign}>
                          <WsDesignCard
                            baseStoreUrl={baseResourceUrl}
                            colorScheme={colorScheme}
                            ecomStoreDesign={d.design}
                            ecomStoreTheme={ecomStoreTheme}
                            mediaQueries={mediaQueries}
                            onNavigate={onNavigate}
                            themeConfig={themeConfig}
                            {...rest}
                          />
                        </Carousel.Slide>
                      ))
                  : filteredBlocks.slice(0, maxDisplayed).map((b) => (
                      <Carousel.Slide key={b.key}>
                        <WsBlock
                          baseStoreUrl={baseResourceUrl}
                          colorScheme={colorScheme}
                          ecomStoreTheme={ecomStoreTheme}
                          mediaQueries={mediaQueries}
                          onNavigate={onNavigate}
                          portraitRatio={
                            config.imageRatio === IMAGE_DISPLAY_ENUM.PORTRAIT
                          }
                          resourceNavigation={resourceNavigation}
                          selectedResources={selectedResources}
                          themeConfig={themeConfig}
                          {...b}
                        />
                      </Carousel.Slide>
                    ))}
              </Carousel>
            ) : (
              <AppStack style={{}}>
                <Grid
                  columns={
                    isTabletOrSmaller
                      ? config.mobileColumnCount * 1 ?? 1
                      : config.desktopColumnCount * 1 ?? 6
                  }
                >
                  {isBlocksEmpty
                    ? filteredDesigns
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .slice(0, maxDisplayed)
                        .map((d) => (
                          <Grid.Col key={d.pkEcomVendorDesign} span={1}>
                            <WsDesignCard
                              baseStoreUrl={baseResourceUrl}
                              colorScheme={colorScheme}
                              ecomStoreDesign={d.design}
                              ecomStoreTheme={ecomStoreTheme}
                              mediaQueries={mediaQueries}
                              onNavigate={onNavigate}
                              themeConfig={themeConfig}
                              {...rest}
                            />
                          </Grid.Col>
                        ))
                    : filteredBlocks.slice(0, maxDisplayed).map((b) => (
                        <Grid.Col key={b.key} span={1}>
                          <WsBlock
                            baseStoreUrl={baseResourceUrl}
                            colorScheme={colorScheme}
                            ecomStoreTheme={ecomStoreTheme}
                            mediaQueries={mediaQueries}
                            onNavigate={onNavigate}
                            resourceNavigation={resourceNavigation}
                            selectedResources={selectedResources}
                            themeConfig={themeConfig}
                            {...b}
                          />
                        </Grid.Col>
                      ))}
                </Grid>
              </AppStack>
            )}

            {!state.ecomStoreDesigns.loading &&
              config.enableViewAll === 'true' &&
              designCount > maxDisplayed && (
                <WsSectionViewAllButtons
                  buttonStyleType={config.viewAllButtonStyle}
                  colorScheme={colorScheme}
                  onNavigate={onNavigate}
                  url={`${baseResourceUrl}/designs`}
                />
              )}
          </AppStack>
        </AppStack>
      </WsSectionContainer>
    )
  );
};

WsDesignList.propTypes = {
  baseStoreUrl: PropTypes.string,
  blocks: PropTypes.array,
  colorSchemes: PropTypes.array,
  config: PropTypes.object,
  ecomStoreTheme: PropTypes.object,
  mediaQueries: PropTypes.object,
  onNavigate: PropTypes.func,
  resourceNavigation: PropTypes.array,
  selectedResources: PropTypes.object,
  themeConfig: PropTypes.object
};

export default WsDesignList;
